import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { HttpModule } from '@angular/http';
import { BrowserModule } from '@angular/platform-browser';
import { IonicStorageModule } from '@ionic/storage';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { IonicApp, IonicErrorHandler, IonicModule } from 'ionic-angular';

import { FormsModule } from '@angular/forms';
import { DataTablesModule } from 'angular-datatables';
import { AlertModule, TypeaheadModule } from 'ngx-bootstrap';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { IsDateAfterValidatorDirective } from '../directives/is-date-after-valitator.directive';
import { IsDateTimeAfterValidatorDirective } from '../directives/is-date-time-after-valitator.directive';
import { MaxValueDirective } from '../directives/max-validator.directive';
import { TableDirective } from '../directives/table-directive';
import { UniqueUsernameDirective } from '../directives/unique-username-validator.directive';
import { UniqueDirective } from '../directives/unique-validator.directive';
import { CardDetailsPage } from '../pages/card-details/card-details';
import { CardReaderLoginPage } from '../pages/card-reader-login/card-reader-login';
import { CardTypesPage } from '../pages/card-types/card-types';
import { CardsPage } from '../pages/cards/cards';
import { ConfigurationPage } from '../pages/configuration/configuration';
import { CouponTypesPage } from '../pages/coupon-types/coupon-types';
import { CouponsPage } from '../pages/coupons/coupons';
import { CurrentAccountDetailsPage } from '../pages/current-account-details/current-account-details';
import { CurrentAccountsPage } from '../pages/current-accounts/current-accounts';
import { CustomerDetailsPage } from '../pages/customer-details/customer-details';
import { CustomerGroupDetailsPage } from '../pages/customer-group-details/customer-group-details';
import { CustomerGroupsPage } from '../pages/customer-groups/customer-groups';
import { CustomersPage } from '../pages/customers/customers';
import { LayoutPage } from '../pages/layout/layout';
import { LoginPage } from '../pages/login/login';
import { OrganizationPage } from '../pages/organization/organization';
import { PosAppPage } from '../pages/pos-app/pos-app';
import { PosPage } from '../pages/pos/pos';
import { PriceListPage } from '../pages/price-list/price-list';
import { PrintCardPage } from '../pages/print-card/print-card';
import { PrintsPage } from '../pages/prints/prints';
import { ProductCategoriesPage } from '../pages/product-categories/product-categories';
import { ProductsPage } from '../pages/products/products';
import { RegisterActivitiesPage } from '../pages/register-activities/register-activities';
import { RegisterActivityDetailsPage } from '../pages/register-activity-details/register-activity-details';
import { RegisterActivityTransactionsPage } from '../pages/register-activity-transactions/register-activity-transactions';
import { SpecialOffersPage } from '../pages/special-offers/special-offers';
import { TabsPage } from '../pages/tabs/tabs';
import { TaxRatesPage } from '../pages/taxrates/taxrates';
import { UnitsOfMeasurePage } from '../pages/units-of-measure/units-of-measure';
import { UserDetailsPage } from '../pages/user-details/user-details';
import { UsersPage } from '../pages/users/users';
import { ZonesPage } from '../pages/zones/zones';
import { AuthenticationService } from '../providers/authentication/authenticationService';
import { RefreshTokenInterceptor } from '../providers/authentication/refreshTokenInterceptor';
import { ConfigService } from '../providers/config/config.service';
import { PaytronBackendService } from '../providers/paytron-backend/paytron-backend.service';
import { Utils } from '../providers/paytron-backend/utils';
import { ScriptLoader } from '../providers/script-loader';
import { MyApp } from './app.component';
import { CommonsModule } from './common.module';
import { CardMaskPipe } from './utilities/CardMaskPipe';
import { ConfigurableDatePipe } from './utilities/ConfigurableDatePipe';
import { ConfigurableDatetimePipe } from './utilities/ConfigurableDatetimePipe';
import { EnumKeyValueListPipe } from './utilities/EnumKeyValueListPipe';
import { TableService } from '../services/table.service';
import { UserService } from '../services/user.service';
import { CommonService } from '../services/common.service';
import { ProductsService } from '../services/products.service';
import { UniqueCaseInsensitiveDirective } from '../directives/unique-case-insensitive-validator.directive';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    MyApp,
    ConfigurationPage,
    TabsPage,
    LoginPage,
    CardReaderLoginPage,
    UsersPage,
    UserDetailsPage,
    CardDetailsPage,
    CustomersPage,
    CustomerGroupsPage,
    CustomerGroupDetailsPage,
    CustomerDetailsPage,
    CurrentAccountDetailsPage,
    RegisterActivitiesPage,
    RegisterActivityDetailsPage,
    LayoutPage,
    CardsPage,
    SpecialOffersPage,
    ProductsPage,
    ProductCategoriesPage,
    PosPage,
    ZonesPage,
    PosAppPage,
    TaxRatesPage,
    PriceListPage,
    OrganizationPage,
    CouponTypesPage,
    UnitsOfMeasurePage,
    CardTypesPage,
    CouponsPage,
    PrintCardPage,
    PrintsPage,
    RegisterActivityTransactionsPage,
    CurrentAccountsPage,
    EnumKeyValueListPipe,
    CardMaskPipe,
    ConfigurableDatePipe,
    ConfigurableDatetimePipe,
    TableDirective,
    IsDateAfterValidatorDirective,
    IsDateTimeAfterValidatorDirective,
    MaxValueDirective,
    UniqueUsernameDirective,
    UniqueDirective,
    UniqueCaseInsensitiveDirective
  ],
  imports: [
    BrowserModule,
    HttpModule,
    HttpClientModule,
    CommonsModule,
    AlertModule.forRoot(),
    ModalModule.forRoot(),
    BsDatepickerModule.forRoot(),
    TimepickerModule.forRoot(),
    TypeaheadModule.forRoot(),
    IonicModule.forRoot(MyApp, {
      animate: false, // disable animation
    }),
    IonicStorageModule.forRoot(),
    DataTablesModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    FormsModule,
  ],
  bootstrap: [IonicApp],
  entryComponents: [
    MyApp,
    ConfigurationPage,
    TabsPage,
    LoginPage,
    CardReaderLoginPage,
    UsersPage,
    UserDetailsPage,
    CardDetailsPage,
    CustomersPage,
    CustomerDetailsPage,
    CustomerGroupsPage,
    CustomerGroupDetailsPage,
    CurrentAccountDetailsPage,
    RegisterActivitiesPage,
    RegisterActivityDetailsPage,
    LayoutPage,
    CardsPage,
    SpecialOffersPage,
    ProductsPage,
    ProductCategoriesPage,
    PosPage,
    ZonesPage,
    PosAppPage,
    PriceListPage,
    TaxRatesPage,
    UnitsOfMeasurePage,
    CardTypesPage,
    OrganizationPage,
    CouponTypesPage,
    CouponsPage,
    PrintCardPage,
    PrintsPage,
    RegisterActivityTransactionsPage,
    CurrentAccountsPage,
  ],
  providers: [
    AuthenticationService,
    PaytronBackendService,
    TableService,
    UserService,
    CommonService,
    ProductsService,
    Utils,
    ConfigService,
    ScriptLoader,
    { provide: ErrorHandler, useClass: IonicErrorHandler },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RefreshTokenInterceptor,
      multi: true,
    },
    // NavControllerService
  ],
  exports: [TranslateModule],
})
export class AppModule {}



// WEBPACK FOOTER //
// ./src/app/app.module.ts